import { useMemo } from 'react';
import { ArrowRight } from 'lucide-react';

/**
 * Field-level JSON diff for creator agent versions.
 * Compact, read-only view used in the review queue + versions history.
 *
 * Strategy: walk the union of top-level keys, stringify each value, and render
 * a side-by-side or stacked comparison. This is good-enough for M1 review;
 * deep-diff with collapsible sections lands with the M2 Build Theater.
 */
export function VersionDiff({
  before,
  after,
  beforeLabel = 'Live',
  afterLabel = 'Pending',
}: {
  before: Record<string, unknown> | null;
  after: Record<string, unknown> | null;
  beforeLabel?: string;
  afterLabel?: string;
}) {
  const rows = useMemo(() => buildDiffRows(before ?? {}, after ?? {}), [before, after]);
  if (rows.length === 0) {
    return (
      <p className="text-xs text-th-secondary px-2 py-3">
        No differences detected between {beforeLabel.toLowerCase()} and {afterLabel.toLowerCase()}.
      </p>
    );
  }
  return (
    <div className="space-y-2">
      {rows.map((row) => (
        <div
          key={row.key}
          className="border border-subtle rounded-lg overflow-hidden bg-elevated"
        >
          <div className="px-3 py-1.5 bg-surface border-b border-subtle flex items-center justify-between">
            <code className="text-xs text-th-primary font-mono">{row.key}</code>
            <span className={`text-[10px] uppercase tracking-wide px-1.5 py-0.5 rounded ${changeTone(row.kind)}`}>
              {row.kind}
            </span>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-[1fr_auto_1fr] gap-2 p-3 items-start">
            <DiffPane label={beforeLabel} value={row.before} tone="before" />
            <ArrowRight className="w-4 h-4 text-th-tertiary self-center hidden md:block" />
            <DiffPane label={afterLabel} value={row.after} tone="after" />
          </div>
        </div>
      ))}
    </div>
  );
}

function DiffPane({
  label,
  value,
  tone,
}: {
  label: string;
  value: string;
  tone: 'before' | 'after';
}) {
  return (
    <div>
      <p className="text-[10px] uppercase tracking-wide text-th-tertiary mb-1">{label}</p>
      <pre
        className={`text-xs font-mono whitespace-pre-wrap break-words p-2 rounded border max-h-48 overflow-y-auto ${
          tone === 'before'
            ? 'border-status-error/20 bg-status-error/5 text-th-secondary'
            : 'border-status-success/20 bg-status-success/5 text-th-primary'
        }`}
      >
        {value || <span className="text-th-tertiary italic">(empty)</span>}
      </pre>
    </div>
  );
}

interface DiffRow {
  key: string;
  before: string;
  after: string;
  kind: 'added' | 'removed' | 'changed';
}

function buildDiffRows(
  before: Record<string, unknown>,
  after: Record<string, unknown>
): DiffRow[] {
  const keys = new Set([...Object.keys(before), ...Object.keys(after)]);
  const rows: DiffRow[] = [];
  for (const key of Array.from(keys).sort()) {
    const a = key in before ? before[key] : undefined;
    const b = key in after ? after[key] : undefined;
    const aStr = stringify(a);
    const bStr = stringify(b);
    if (aStr === bStr) continue;
    let kind: DiffRow['kind'];
    if (a === undefined) kind = 'added';
    else if (b === undefined) kind = 'removed';
    else kind = 'changed';
    rows.push({ key, before: aStr, after: bStr, kind });
  }
  return rows;
}

function stringify(v: unknown): string {
  if (v === undefined) return '';
  if (v === null) return 'null';
  if (typeof v === 'string') return v;
  if (typeof v === 'number' || typeof v === 'boolean') return String(v);
  try {
    return JSON.stringify(v, null, 2);
  } catch {
    return String(v);
  }
}

function changeTone(kind: DiffRow['kind']): string {
  if (kind === 'added') return 'bg-status-success/15 text-status-success';
  if (kind === 'removed') return 'bg-status-error/15 text-status-error';
  return 'bg-status-warning/15 text-status-warning';
}
